<template>
  <InputSelect
    v-model="selectedOptions"
    :disabled="disabled"
    :dropdown-text="dropdownText"
    emit-value
    multiple
    new-design
    option-label="label"
    option-prop="value"
    :options="filteredListOptions"
    :popper-width="popperWidth"
    :selected-text="selectedText"
    :validations="validations"
    @click="$emit('click')"
    @dropdown-closed="resetChanges"
  >
    <template #beforeOptions>
      <div
        class="before-options"
        @click.stop
      >
        <InputText
          v-if="searchable"
          v-model="filter"
          class="input"
          clearable
          new-design
          :placeholder="$t('input_select_with_checkboxes.search')"
          tabindex="-1"
          @focus="$emit('search-focus')"
        >
          <template #prepend>
            <ImageIcon
              class="q-pl-sm text-ds-denim-4"
              fallback-icon="ion-search"
            />
          </template>
        </InputText>
        <slot name="optionsText" />
      </div>

      <slot name="beforeAllOptions" />

      <div
        v-if="showAllOptions"
        class="list-option all-option"
        @click.stop
      >
        <Checkbox
          v-model="allOptionsSelected"
          class="full-width"
          :color="checkboxColor"
          :indeterminate="isAllOptionIndeterminate"
          :label="allOptionsLabel"
          left-label
          new-design
          @update:model-value="$emit('all-options', $event)"
        />
      </div>

      <slot name="afterSearchInput" />
    </template>
    <template #option="props">
      <slot
        name="option"
        v-bind="props"
      >
        <div
          class="list-option row full-width no-wrap cursor-pointer items-center"
          :class="{
            active: props.active,
            'group-option': props.option.isGroup,
          }"
          data-testid="option"
        >
          <QIcon
            v-if="props.option.icon"
            class="icon no-shrink col-auto"
            :name="props.option.icon"
            size="14px"
          />
          <div
            v-if="showIcon"
            class="stream-icon no-shrink col-auto"
            :class="{ 'square-icon': squareIcons }"
            :style="{ backgroundColor: props.option.color }"
          />
          <div
            class="text-label ellipsis col-grow col-shrink overflow-hidden"
            :class="{ 'q-pl-sm': showIcon }"
          >
            <slot
              v-bind="props"
              name="label"
            >
              {{ props.option.label }}
            </slot>
          </div>
          <div class="checkbox-left no-shrink col-auto">
            <Checkbox
              v-if="!props.option.isGroup"
              :color="props.option.checkboxColor || checkboxColor"
              :model-value="props.selected"
              new-design
              @click="$emit('checkbox-toggled', props.option.label)"
            />
          </div>
        </div>
      </slot>
    </template>
    <template
      v-if="showOkButton"
      #afterOptions
    >
      <slot name="afterOptions">
        <div class="row ok-row q-pa-xs border-ds-denim-2 justify-end">
          <BaseButton
            class="full-width"
            color="primary"
            data-testid="input-select-with-checkboxes-confirm-button"
            :disabled="invalid"
            :label="okButtonLabel"
            new-design
            @click="onConfirm"
          />
          <StreemTooltip v-if="invalid">
            {{
              $t("input_select_with_checkboxes.minimum_option_description", {
                minSelected,
              })
            }}
          </StreemTooltip>
        </div>
      </slot>
    </template>
  </InputSelect>
</template>

<script>
import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  ImageIcon,
  InputSelect,
  InputText,
  StreemTooltip,
} from "shared/components/base";
import Checkbox from "shared/components/core/forms/Checkbox.vue";

export default {
  name: "InputSelectWithCheckboxes",
  components: {
    BaseButton,
    Checkbox,
    ImageIcon,
    InputSelect,
    InputText,
    StreemTooltip,
  },
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      required: true,
    },
    groupedOptions: {
      type: Boolean,
    },
    placeholderText: {
      type: String,
      required: true,
    },
    selectedText: {
      type: Function,
      default: null,
    },
    checkboxColor: {
      type: String,
      default: "primary",
    },
    maxSelected: {
      type: Number,
      default: null,
    },
    minSelected: {
      type: Number,
      default: null,
    },
    searchable: {
      type: Boolean,
    },
    showIcon: {
      type: Boolean,
    },
    fixedPlaceholder: {
      type: Boolean,
    },
    showOkButton: {
      type: Boolean,
    },
    okButtonLabel: {
      type: String,
      default: () => getLocaleText("global.confirm"),
    },
    squareIcons: {
      type: Boolean,
    },
    popperWidth: {
      type: Number,
      default: null,
    },
    disableOptions: {
      type: Boolean,
    },
    showAllOptions: {
      type: Boolean,
    },
    allOptionsLabel: {
      type: String,
      default: () => getLocaleText("global.select_all"),
    },
    disabled: {
      type: Boolean,
    },
    validations: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    "click",
    "search-focus",
    "checkbox-toggled",
    "confirm",
    "update:modelValue",
    "cancel",
    "all-options",
  ],
  data() {
    return {
      filter: "",
      localSelected: [],
      unsavedChanges: false,
    };
  },
  computed: {
    selectableOptions() {
      if (this.groupedOptions) {
        return this.options.flatMap(({ options }) => options);
      }

      return this.options;
    },
    selectedValues() {
      if (this.disableOptions) return this.modelValue;

      return this.unsavedChanges ? this.localSelected : this.modelValue;
    },
    selectedOptions: {
      get() {
        return this.selectableOptions.filter((option) =>
          this.selectedValues.includes(option.value)
        );
      },
      set(value) {
        if (!this.showOkButton) this.emitChanges(value);
        else {
          this.localSelected = value;
          this.unsavedChanges = true;
        }
      },
    },
    dropdownText() {
      if (this.fixedPlaceholder) return this.placeholderText;

      return this.selectedOptions.length ? "" : this.placeholderText;
    },
    filteredListOptions() {
      const filter = this.filter.toLowerCase();

      if (this.groupedOptions) {
        return this.options.flatMap((group) => {
          const groupOptions = this.filterOptions(group.options, filter);
          if (!groupOptions.length) return [];

          return [{ ...group, notSelectable: true }, ...groupOptions];
        });
      }

      return this.filterOptions(this.options, filter);
    },
    invalid() {
      return this.minSelected && this.selectedValues.length < this.minSelected;
    },
    allOptionsSelected: {
      get() {
        return this.selectableOptions.length === this.selectedValues.length;
      },
      set(value) {
        if (value) {
          this.selectedOptions = this.selectableOptions.map(
            (option) => option.value
          );
        } else {
          this.selectedOptions = [];
        }
      },
    },
    isAllOptionIndeterminate() {
      return this.selectedOptions.length > 0 && !this.allOptionsSelected;
    },
  },
  methods: {
    filterOptions(options, filter) {
      return options
        .filter(({ label }) => label.toLowerCase().includes(filter))
        .map((option) => ({
          ...option,
          disabled: this.isDisabled(option),
        }));
    },
    isDisabled(option) {
      return (
        this.disableOptions ||
        (this.maxSelected &&
          this.selectedValues.length >= this.maxSelected &&
          !this.isSelected(option))
      );
    },
    isSelected(option) {
      return this.selectedOptions.includes(option);
    },
    onConfirm() {
      this.$emit("confirm");

      if (this.unsavedChanges) {
        this.emitChanges(this.localSelected);
      }
    },
    emitChanges(value) {
      this.$emit("update:modelValue", value);
      this.unsavedChanges = false;
    },
    resetChanges() {
      if (this.unsavedChanges) {
        this.$emit("cancel");
      }

      this.localSelected = [];
      this.unsavedChanges = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.icon {
  margin-right: 6px;
}

.stream-icon {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #757575;

  &.square-icon {
    border-radius: 2px;
    border: 0;
  }
}

.checkbox-left {
  margin-left: auto;
}

.text-label {
  color: var(--s-color-denim-9);
}

.input-select {
  :deep(.list-option) {
    padding-right: 8px;

    label.checkbox {
      margin: 0;
    }
  }
}

.input {
  :deep(.input-wrapper) {
    border: none;
    border-radius: 0;
    border-bottom: 1px solid var(--s-color-denim-2);

    &.prepend {
      padding: 0;
    }
  }

  :deep(.clear-icon) {
    padding-right: 4px;
  }
}

.before-options {
  border-bottom: 1px solid var(--s-color-denim-2);
  margin-bottom: -1px;
}

.all-option {
  border-bottom: 1px solid var(--s-color-denim-2);
  margin-bottom: -1px;

  :deep() {
    .checkbox-label {
      color: var(--s-color-denim-9);
    }

    input {
      margin-left: auto;
    }
  }
}
</style>
